import { lookupCard } from "../../content/index.js";
import { draw } from "../deck/draw.js";
import { actionDraw } from "../deck/actionDraw.js";
import type { ActionEffect, CardInstance } from "../types/index.js";
import type { ApplyEffectContext } from "../cards/effectContext.js";

function revealTop(ctx: ApplyEffectContext): { ctx: ApplyEffectContext; card: CardInstance | null } {
  const drawn = draw(
    { deck: ctx.run.deck, discard: ctx.run.discard, rngState: ctx.run.rngState },
    1,
  );
  return {
    ctx: {
      ...ctx,
      reshuffled: ctx.reshuffled || drawn.reshuffled,
      run: { ...ctx.run, deck: drawn.deck, discard: drawn.discard, rngState: drawn.rngState },
    },
    card: drawn.drawn[0] ?? null,
  };
}

export function startCellarChoice(
  ctx: ApplyEffectContext,
  effect: Extract<ActionEffect, { kind: "cellar" }>,
): ApplyEffectContext {
  return {
    ...ctx,
    run: {
      ...ctx.run,
      energy: ctx.run.energy + effect.actions,
      pendingChoice: { kind: "cellar", sourceInstanceId: ctx.sourceInstanceId, selected: [] },
    },
  };
}

export function startKnowledgeShareChoice(ctx: ApplyEffectContext): ApplyEffectContext {
  if (ctx.run.discard.length === 0) return ctx;
  return {
    ...ctx,
    run: {
      ...ctx.run,
      pendingChoice: { kind: "knowledgeShare", sourceInstanceId: ctx.sourceInstanceId, selected: [] },
    },
  };
}

export function startFieldJudgmentChoice(ctx: ApplyEffectContext): ApplyEffectContext {
  const { ctx: next, card } = revealTop(ctx);
  if (!card) return ctx;
  if (lookupCard(card.cardId)?.type === "action") {
    return {
      ...next,
      run: {
        ...next.run,
        pendingChoice: { kind: "fieldJudgment", sourceInstanceId: ctx.sourceInstanceId, revealed: card, selected: null },
      },
    };
  }
  return {
    ...next,
    run: { ...next.run, deck: [...next.run.deck, card] },
    activeCase: { ...next.activeCase, lastFieldJudgment: { cardId: card.cardId, result: lookupCard(card.cardId)?.type === "task" ? "returned-task" : "returned-worker" } },
  };
}

export function startSendoffChoice(ctx: ApplyEffectContext): ApplyEffectContext {
  const hasWorker = ctx.run.hand.some((card) => lookupCard(card.cardId)?.type === "worker");
  if (!hasWorker) return ctx;
  return {
    ...ctx,
    run: {
      ...ctx.run,
      pendingChoice: { kind: "sendoff", sourceInstanceId: ctx.sourceInstanceId, selected: [] },
    },
  };
}

export function startTrainingChoice(ctx: ApplyEffectContext): ApplyEffectContext {
  const hasTrainee = ctx.run.hand.some((card) => card.cardId === "intern" || card.cardId === "mid");
  if (!hasTrainee) return ctx;
  return {
    ...ctx,
    run: {
      ...ctx.run,
      pendingChoice: { kind: "training", sourceInstanceId: ctx.sourceInstanceId, selected: [] },
    },
  };
}

export function startFocusChoice(ctx: ApplyEffectContext): ApplyEffectContext {
  if (ctx.run.hand.length >= 7) return ctx;
  const drawn = actionDraw(ctx.run, 1);
  const card = drawn.drawn[0];
  if (!card) return ctx;
  const nextRun = drawn.run;
  if (lookupCard(card.cardId)?.type !== "action") {
    return startFocusChoice({
      ...ctx,
      reshuffled: ctx.reshuffled || drawn.reshuffled,
      run: { ...nextRun, hand: [...nextRun.hand, card] },
    });
  }
  return {
    ...ctx,
    reshuffled: ctx.reshuffled || drawn.reshuffled,
    run: {
      ...nextRun,
      pendingChoice: { kind: "focus", sourceInstanceId: ctx.sourceInstanceId, pendingCard: card, selected: null },
    },
  };
}

export function startQualityChoice(ctx: ApplyEffectContext): ApplyEffectContext {
  const revealed: CardInstance[] = [];
  let next = ctx;
  for (let index = 0; index < 2; index += 1) {
    const result = revealTop(next);
    next = result.ctx;
    if (!result.card) break;
    revealed.push(result.card);
  }
  if (revealed.length === 0) return next;
  return {
    ...next,
    run: {
      ...next.run,
      pendingChoice: { kind: "quality", sourceInstanceId: ctx.sourceInstanceId, revealed, step: 0, decisions: [] },
    },
  };
}
